import React from "react";
import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import * as Styles from "./styles/GradeEngineBlock";
import * as Text from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import type { ModelGradeMotorizationType, ModelGradeType } from "../../../../../../common-deprecated/types/CommonTypes";
import type { GradeMotorizationType } from "../../../../../../common-deprecated/types/GradeType";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import FuelTypeLabel from "../../../../../../common-deprecated/styles/v2/toyota/components/FuelTypeLabel";
import DisplayPrice from "./price/Price";

import { getEcoBadgeUrl } from "../../../../../../common-deprecated/utils";
import { getEcoBadgeType } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import {
    trackAddCompareProduct,
    trackChooseEngine,
    trackEngineViewInfo,
    trackNavGoToStep,
} from "../../../utils/tracking";
import { MODAL_ENGINE_DETAIL } from "../../../utils/modalConstants";
import { BnbLabel } from "../../../utils/labels";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { compareAddCar } from "../../../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { EcoBadge } from "../../../../../../common-deprecated/styles/toyota/Badge";
import GradeEngineSpecs from "./GradeEngineSpecs";
import type { NewCarFuelTypeFilterId } from "../../../../../../common-deprecated/constants";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import ButtonSecondary from "../../../../../../common-deprecated/components/aem/toyota/ButtonSecondary";
import ButtonPrimary from "../../../../../../common-deprecated/components/aem/toyota/ButtonPrimary";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/toyota/ButtonTertiary";
import { getEcoTagType } from "../../../../../shared-logic/utils/typedUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type EnginePropsType = {
    grade: ModelGradeType;
    motorization: ModelGradeMotorizationType;
    index: number;
};

const EngineBlock = ({ motorization, grade, index }: EnginePropsType): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const model = useBnbSelector((state) => state.model);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const { engine, transmission, wheeldrive, price, specs } = motorization;

    const fuelType = getEcoTagType(engine);
    const ecoBadge = getEcoBadgeType(commonSettings, fuelType, model.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);

    const fullName = `${model.name} ${grade.name} ${engine?.name} ${transmission?.name}:`;
    const gradeHasMultipleEngines = grade.motorizations.length > 1;

    const customizeUrl = useStepUrl(NavStep.Customize, {
        modelId: model.id,
        bodyTypeId: grade.bodyType.id,
        gradeId: grade.id,
        carId: motorization.carID,
    }).href;

    return (
        <Styles.Wrapper>
            <Styles.Header>
                <Styles.Title>{engine.name}</Styles.Title>
                <FuelTypeLabel type={fuelType}>{engine.type}</FuelTypeLabel>
                <Styles.TransmissionDescription
                    isSmall
                >{`${transmission.name} (${wheeldrive.name})`}</Styles.TransmissionDescription>
                <Styles.PriceWrapper>
                    <DisplayPrice price={price} financeInfo={motorization.finance} cashPriceDisclaimer={model.labels} />
                </Styles.PriceWrapper>
                {ecoBadgeUrl && <EcoBadge imageUrl={ecoBadgeUrl} />}
            </Styles.Header>

            <div>
                <GradeEngineSpecs specs={specs} certification={engine.certification} electric={engine.electric} />
                <ButtonTertiary
                    onClick={() => {
                        trackEngineViewInfo(NavStep.ModelEngine);
                        dispatch(
                            showModal({
                                type: MODAL_ENGINE_DETAIL,
                                motorization: motorization as GradeMotorizationType,
                            }),
                        );
                    }}
                    aria-label={fullName}
                >
                    <BnbLabel label="moreInfo" />
                </ButtonTertiary>
            </div>

            <Styles.Actions>
                <ButtonPrimary
                    href={customizeUrl}
                    fullWidth
                    onClick={() => {
                        trackNavGoToStep(NavStep.ModelEngine, NavStep.Customize);
                        trackChooseEngine(NavStep.ModelEngine, grade.name || model.name, index);
                    }}
                    aria-label={`customise ${fullName}`}
                >
                    <Button.EllipsisText>
                        <BnbLabel
                            label={gradeHasMultipleEngines ? "chooseThisEngine" : "thisIsYourEngineButton"}
                            fallbackLabel="chooseThisEngine"
                        />
                    </Button.EllipsisText>
                </ButtonPrimary>
                <ButtonSecondary
                    onClick={(event) => {
                        event.preventDefault();
                        trackAddCompareProduct(NavStep.ModelEngine, grade.name || model.name, index);
                        const ids = {
                            model: model.id,
                            gradeId: grade.id,
                            bodyTypeId: grade.bodyType.id,
                            submodelId: "",
                        };
                        const motorizationSpecs = {
                            engineId: engine.id,
                            engineName: engine.name,
                            transmissionId: transmission.id,
                            transmissionName: transmission.name,
                            wheeldriveId: wheeldrive.id,
                            wheeldriveName: wheeldrive.name,
                            fuelTypeCode: engine.typeCode as NewCarFuelTypeFilterId,
                        };
                        dispatch(compareAddCar(ids, motorizationSpecs, -1));
                    }}
                    fullWidth
                    aria-label={`compare ${model.name} ${grade.name}`}
                >
                    <IconWrapper iconColor="primaryBlack">
                        <Icon variant="compare" />
                    </IconWrapper>
                    <Text.Button>
                        <BnbLabel label="compareAdd" />
                    </Text.Button>
                </ButtonSecondary>
            </Styles.Actions>
        </Styles.Wrapper>
    );
};

export default EngineBlock;
